"use client";

import json from "@/public/data/hotel/detail.json";
import { Pulse } from "@/shared/components/common/Animations";
import { Button } from "@/shared/components/common/button";
import Datepicker from "@/shared/components/common/datepicker/Datepicker";
import { DrawerClose } from "@/shared/components/common/drawer/drawer";
import Flex from "@/shared/components/common/Flex";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/common/form";
import Grid from "@/shared/components/common/Grid";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { PopoverClose } from "@/shared/components/common/popover";
import Typography from "@/shared/components/common/Typography";
import CustomInput from "@/shared/components/searchTab/components/CustomInput";
import Stayduration from "@/shared/components/searchTab/components/stayduration/Stayduration";
import { useDeviceType } from "@/shared/context/DeviceContext";
import useProgress from "@/shared/hooks/useProgress";
import { scrollToElement } from "@/shared/libs/commonUtils";
import { formatIntoJalaliFunc } from "@/shared/libs/date";
import { hotelDefaultValue } from "@/shared/schemas/hotel/hotelTab.schema";
import {
  type SearchFilter,
  searchFilterSchema,
} from "@/shared/schemas/SearchFilter.schema";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import { parseHotelResultsV2Date } from "@/containers/website/hotel/results-v2/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CalendarBlank,
  CalendarCheck,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { addDays, differenceInDays, format, parse } from "date-fns-jalali";
import { useRouter } from "next/navigation";
import { memo, type ReactNode, useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
type TFormField = "checkin" | "checkout";
interface HotelDetailFilterProps {
  params: {
    city: string;
    slug: string;
  };
  searchParams?: SearchParams;
}

export const HotelDetailFilter = memo(
  ({ searchParams, params }: HotelDetailFilterProps) => {
    const { city, slug } = params;

    const [isLoading, setIsLoading] = useState(false);
    const { setDetailRooms } = useHotelStore((state) => state);

    const router = useRouter();

    const { checkIn, checkOut, rooms } = searchParams ?? {};

    const tabRef = useRef<HTMLDivElement | null>(null);

    const {
      mainForm: { night, formsInputs, searchButton },
    } = json.filter;

    const form = useForm<SearchFilter>({
      resolver: yupResolver(searchFilterSchema),
      defaultValues: {
        checkin: normalizeHotelDetailDateInput(
          checkIn,
          hotelDefaultValue.checkin,
        ),
        checkout: normalizeHotelDetailDateInput(
          checkOut,
          hotelDefaultValue.checkout,
        ),
      },
    });

    const { control, watch, setValue, getValues, handleSubmit } = form;

    const progressInputs = [
      {
        id: 1,
        name: "checkin",
        value: watch("checkin"),
      },
      {
        id: 2,
        name: "checkout",
        value: watch("checkout"),
      },
    ];

    const { handleProgress, openStatus } = useProgress(progressInputs);

    const { checkin, checkout } = getValues();

    const startDate = parse(checkin ?? "", "yyyy/MM/dd", new Date());
    const endDate = parse(checkout, "yyyy/MM/dd", new Date());

    const initialDifference = differenceInDays(endDate, startDate);

    const stayDurationFormatter = () => {
      return initialDifference + night;
    };

    const handleSetValues = useCallback(
      ({
        paramName,
        id,
        func,
      }: {
        paramName: TFormField;
        id: number;
        func?: (value: any) => string;
      }) =>
        (value: any) => {
          if (func) {
            if (id == 1) {
              setValue(
                "checkout",
                format(addDays(value.depart, initialDifference), "yyyy/MM/dd"),
              );
              setValue(
                paramName,
                func(
                  typeof value === "string"
                    ? value
                    : (value as { depart: string }).depart,
                ),
              );
            }
          } else {
            setValue(paramName, value);
          }
          id !== 3 && handleProgress(id, true);
        },
      [handleProgress, setValue, initialDifference],
    );

    const inputs = (
      key: string,
      paramName: TFormField,
      id: number,
      func?: (value: any) => string,
    ) => {
      const inputObject = {
        checkin: (
          <Datepicker
            formatDateIntoJalali={false}
            onPickDate={handleSetValues({ paramName, id, func })}
            OnCloseComp={
              useDeviceType() === "mobile" ? DrawerClose : PopoverClose
            }
          />
        ),
        checkout: (
          <Stayduration
            initialDate={parse(checkin, "yyyy/MM/dd", new Date())}
            onClick={handleSetValues({ paramName, id, func })}
          />
        ),
      };

      return inputObject[key as TFormField];
    };

    const onSubmit = (data: SearchFilter) => {
      const { checkin, checkout } = data;
      setDetailRooms([]);
      setIsLoading(true);
      router.push(
        `/hotel/${city}/${slug}/?checkIn=${formatHotelDetailDateForUrl(
          checkin,
        )}&checkOut=${formatHotelDetailDateForUrl(checkout)}&rooms=${rooms ?? "2"}`,
      );

      setTimeout(() => setIsLoading(false), 1000);
    };

    const icons: {
      [key: string]: ReactNode;
    } = {
      checkin: <CalendarBlank className="w-5 h-5" />,
      checkout: <CalendarCheck className="w-5 h-5" />,
    };

    return (
      <Flex
        className={
          "w-full flex-col py-4 mx-4 lg:mx-0 bg-background md:border-[1px] border-garyColor rounded-bl-xs rounded-br-xs mt-7 rounded-xs shadow-1"
        }
      >
        <Flex className=" !w-full h-fit py-4 md:px-6 mx-auto">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full">
              <Grid className="w-full min-w-full grid-cols-12 !gap-0 place-items-end">
                {formsInputs.map(({ type, label, name, id }) => (
                  <Grid.GridCol
                    className={`col-span-12 xl:col-span-6 ${
                      id == 3 && "col-span-12!"
                    } ${
                      type !== "origin" && type !== "destination" && "px-1"
                    } ${type === "destination" && "pl-2"}`}
                    key={id}
                  >
                    <FormField
                      control={control}
                      name={name as TFormField}
                      render={({ field }) => (
                        <FormItem className="!pt-3 relative">
                          <FormLabel value={!!field.value}>{label}</FormLabel>
                          <FormControl>
                            {/* @ts-ignore */}
                            <CustomInput
                              onClick={() => scrollToElement(tabRef, 500, 0)}
                              openStatus={
                                openStatus.find((item) => item.id === id)
                                  ?.open ?? false
                              }
                              handleOpen={() => handleProgress(id)}
                              variant={type as TFormField}
                              className="h-[50px]!"
                              icon={icons[type]}
                              format={
                                type === "checkout"
                                  ? stayDurationFormatter
                                  : undefined
                              }
                              {...field}
                            >
                              {inputs(
                                type,
                                name as TFormField,
                                id,
                                name === "checkin"
                                  ? formatIntoJalaliFunc
                                  : undefined,
                              )}
                            </CustomInput>
                          </FormControl>
                          <FormMessage className="absolute -bottom-[15%] right-[5%] bg-ghost" />
                        </FormItem>
                      )}
                    />
                  </Grid.GridCol>
                ))}
                <Grid.GridCol className="col-span-12 mt-4 pt-6 lg:pt-0 px-1">
                  <Button
                    type="submit"
                    className={`w-full! h-[50px] sm:w-12/12 rounded-xs bg-primary hover:bg-background hover:text-primary ring-1 ring-primary text-background dark:text-foreground shadow-none ${
                      isLoading && "bg-primary!"
                    }`}
                  >
                    <Flex className="items-center gap-x-4">
                      {isLoading ? (
                        <Pulse />
                      ) : (
                        <>
                          <Typography className="!text-md">
                            {searchButton}
                          </Typography>
                          <PhosphorIcon name="MagnifyingGlass" size={20} />
                        </>
                      )}
                    </Flex>
                  </Button>
                </Grid.GridCol>
              </Grid>
            </form>
          </Form>
        </Flex>
      </Flex>
    );
  },
);

function normalizeHotelDetailDateInput(
  value: string | null | undefined,
  fallback: string,
) {
  if (!value) return fallback;

  return format(parseHotelResultsV2Date(value), "yyyy/MM/dd");
}

function formatHotelDetailDateForUrl(value: unknown) {
  if (value instanceof Date) return format(value, "yyyy-MM-dd");

  return format(parseHotelResultsV2Date(String(value ?? "")), "yyyy-MM-dd");
}

HotelDetailFilter.displayName = "HotelDetailFilter";
